import { assertSome, isNone, Maybe, noop } from '@crocodille/common';
import { useCallback } from 'react';
import { useRef, useMemo, useEffect } from 'react';

/**
 * Determines the timeout to use when throttling callback.
 *
 */
export type Timeout =
  // Next animation frame
  | 'animationFrame'
  // After two animation frames (useful for triggering css updates)
  | 'doubleAnimationFrame'
  // Number of milliseconds
  | number;

/**
 * Create a schedule function which schedules a callback to run after timeout or animation frame.
 *
 * @param timeout Timeout in milliseconds or `'animationFrame'` for animationFrame scheduler.
 *
 * @returns Function which schedules the provided callback and returns another function which cancels it.
 */
function createScheduler(timeout: Timeout) {
  if (timeout === 'animationFrame') {
    return (cb: () => void) => {
      const handle = requestAnimationFrame(cb);

      return () => cancelAnimationFrame(handle);
    };
  } else if (timeout === 'doubleAnimationFrame') {
    return (cb: () => void) => {
      let handle = requestAnimationFrame(() => {
        handle = requestAnimationFrame(cb);
      });

      return () => cancelAnimationFrame(handle);
    };
  } else {
    return (cb: () => void) => {
      const handle = setTimeout(cb, timeout);

      return () => clearTimeout(handle);
    };
  }
}

/**
 * Creates a callback which will be only called once within the given timeout.
 * The actual invocation will use the latest supplied parameters.
 *
 *
 * @param callback The callback to throttle
 * @param timeout The `Timeout` to use when debouncing the callback.
 *
 * @returns Throttled callback. This callback has stable identity.
 */
export function useThrottledCallback<A extends unknown[]>(
  callback: (...args: A) => void,
  timeout: Timeout,
): (...args: A) => void {
  //
  // Create a reference to callback so we do not trigger re-schedule when its
  // identity changes.
  //
  const callbackRef = useRef(callback);
  callbackRef.current = callback;
  //
  // Replace the current callback by noop on unmount so it's not invoked after component
  // is unmounted.
  //
  useEffect(
    () => () => {
      callbackRef.current = noop;
    },
    [],
  );

  const schedule = useMemo(() => createScheduler(timeout), [timeout]);
  const scheduled = useRef<Maybe<{ args: A; cancel: () => void }>>(null);

  const throttled = useCallback(
    (...args: A) => {
      if (isNone(scheduled.current)) {
        const cancel = schedule(() => {
          callbackRef.current(...assertSome(scheduled.current?.args));
          scheduled.current = null;
        });

        scheduled.current = { args, cancel };
      } else {
        scheduled.current.args = args;
      }
    },
    [schedule],
  );

  return throttled;
}
